<template>
  <div class="flex-column">
    <div v-for="node of props.value" :class="`${node.key}`" class="flex-column">
      <h1 class="node flex-row flex-align-center flex-left gap-2xs">
        <a
          v-if="depth > 0"
          href="#"
          @click.prevent="updateSelectedKeys(node)"
          class="child"
          :class="{
            debugShow:
              props.debugShowVisibility && (!node.visible || !node.allowed),
          }"
        >
          <input
            v-if="node.key && checked[node.key]"
            @click.prevent=""
            class="clickedradio"
            type="radio"
            checked
          />
          <input v-else class="notclickedradio" type="radio" />
          <span>{{ node.label }}</span>
          <i
            v-if="props.debugShowVisibility && (!node.visible || !node.allowed)"
            class="pi pi-eye-slash"
          />
        </a>
        <a v-else href="#" @click.prevent="updateOpenKeys(node)" class="name">
          <span>{{ node.label }}</span>
          <template v-if="node.children && node.children.length > 0">
            <i v-if="node.key && opened[node.key]" class="pi pi-chevron-down" />
            <i v-else class="pi pi-chevron-right" />
          </template>
        </a>
      </h1>
      <div
        v-if="
          node.key &&
          ((depth === 0 && opened[node.key]) ||
            (depth > 0 && checked[node.key])) &&
          node.children &&
          node.children.length > 0
        "
        class="children"
      >
        <BasicTree
          :depth="props.depth + 1"
          :value="node.children"
          :selectionKeys="props.selectionKeys"
          :openKeys="usedOpenKeys"
          :debugShowVisibility="props.debugShowVisibility"
          @update:openKeys="usedOpenKeys = $event"
          @update:selectionKeys="emits('update:selectionKeys', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useShopStore } from "@/stores/shop";
import copy from "@virgodev/bazaar/functions/copy";
import type { TreeNode } from "primevue/treenode";
import type { Ref } from "vue";
import { computed, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    value: TreeNode[];
    selectionKeys?: string[];
    openKeys?: string[];
    depth?: number;
    debugShowVisibility?: boolean;
  }>(),
  {
    selectionKeys: [] as string[],
    openKeys: [] as string[],
    depth: 0,
    debugShowVisibility: false,
  },
);
const emits = defineEmits(["update:selectionKeys", "update:openKeys"]);

const shop = useShopStore();

const openKeys: Ref<string[]> = ref([]);

const usedOpenKeys = computed({
  get: () => (props.depth === 0 ? openKeys.value : props.openKeys),
  set: (v: string[]) => {
    if (props.depth === 0) {
      openKeys.value = v;
    } else {
      emits("update:openKeys", v);
    }
  },
});

const opened = computed(() => {
  const retval: { [key: string]: boolean } = {};
  for (const key of props.value.map((node) => node.key)) {
    if (key) {
      retval[key] = usedOpenKeys.value.includes(key);
    }
  }
  return retval;
});

const checked = computed(() => {
  const retval: { [key: string]: boolean } = {};
  for (const key of props.value.map((node) => node.key)) {
    if (key) {
      retval[key] = props.selectionKeys.includes(key);
    }
  }
  return retval;
});

watch(
  () => props.selectionKeys,
  () => {
    openSelected();
  },
);

onMounted(() => {
  openSelected();
});

function openSelected() {
  if (props.depth === 0) {
    // open all keys that have a selected child
    const opened = new Set(usedOpenKeys.value);
    for (const key of props.selectionKeys) {
      const branch = findLeaf(key, props.value);
      for (const leaf of branch) {
        opened.add(leaf.key);
      }
    }
    usedOpenKeys.value = Array.from(opened);
  }
}

function findLeaf(key: string, branch: TreeNode[]): TreeNode[] {
  for (const sub of branch) {
    if (sub.key === key) {
      return [sub];
    }
    if (sub.children) {
      const result = findLeaf(key, sub.children);
      if (result) {
        return [sub, ...result];
      }
    }
  }

  return [];
}

function updateOpenKeys(node: TreeNode) {
  if (node.key) {
    const keys: string[] = copy(usedOpenKeys.value);
    const index = keys.indexOf(node.key);
    if (index > -1) {
      keys.splice(index, 1);
    } else {
      keys.push(node.key);
    }
    usedOpenKeys.value = keys;
  }
}

function treeKeys(node: TreeNode): string[] {
  let retval: string[] = [];
  if (node.key) {
    retval.push(node.key);
  }
  // if (node.children) {
  //   for (const child of node.children) {
  //     retval = retval.concat(treeKeys(child));
  //   }
  // }
  return retval;
}
function updateSelectedKeys(node: TreeNode) {
  if (node.key) {
    const keys: Set<string> = new Set(copy(props.selectionKeys));
    const removing = keys.has(node.key);
    if (removing) {
      if (node.data) {
        const change = (
          shop.descendents[node.data] || [{ slug: node.key }]
        ).map((c) => c.slug);
        for (const key of change) {
          keys.delete(key);
        }
      } else {
        keys.delete(node.key);
      }
    } else {
      keys.add(node.key);
    }
    emits("update:selectionKeys", Array.from(keys));
  }
}
</script>

<style scoped>
h1 {
  color: var(--Black, #000);
  font-family: Indivisible;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
}
.pi {
  font-size: 12px;
}
.children {
  margin-left: 1em;
}
.node {
  font-size: 14px;
}
.child {
  display: flex;
  /* white-space: nowrap; */
  gap: 0.5em;
  padding: 8px 0;
}
.name {
  display: flex;
  white-space: nowrap;
  overflow: hidden;
  gap: 0.5em;
}
.name span {
  flex: 1 1 auto;
  text-overflow: ellipsis;
  overflow: hidden;
}

.debugShow {
  opacity: 0.6;
}

input[type="radio"] {
  display: grid;
  /* place-content: center; */
  margin-top: 2px;
  -webkit-appearance: none;
}

/* input.notclickedradio {
  border: none;
} */

input[type="radio"]::before {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 50%;
  appearance: none;
  -webkit-appearance: none;
  outline: 1px solid #000;
}

input[type="radio"]:checked {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 50%;
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid #fff;
  background: var(--Paparazzi-Pink, #ec008c);
  margin-top: 2px;
  place-content: center;
}
</style>
